import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import LandingHeader from '../components/LandingHeader';
import SiteFooter from '../components/SiteFooter';
import { getApiBaseUrl } from '../apiBase';
import { useTranslation } from 'react-i18next';
import { formatPlanPrice, getPricingCurrency } from '../config/domainPricing';
import { isInternationalDomain, isRussianDomain } from '../config/domainLanguages';
import { getPlanDisplayName } from '../utils/planDisplayName';

const UNLIMITED = 999999;

export default function PricingPage() {
    const { t, i18n } = useTranslation(['pricingPage', 'landing', 'common', 'billing']);
    const [plans, setPlans] = useState([]);
    const [annual, setAnnual] = useState(false);
    const [loading, setLoading] = useState(true);
    const currency = useMemo(() => getPricingCurrency(), []);
    const fmtNum = (n) => new Intl.NumberFormat(currency.locale).format(Math.round(n || 0));

    const fmtLimit = (n) => (
        n >= UNLIMITED
            ? t('pricingPage:unlimited')
            : t('pricingPage:upTo', { count: fmtNum(n) })
    );

    const faq = useMemo(() => {
        const items = [
            { q: t('pricingPage:faq.changePlanQ'), a: t('pricingPage:faq.changePlanA') },
            { q: t('pricingPage:faq.activeStudentsQ'), a: t('pricingPage:faq.activeStudentsA') },
            { q: t('pricingPage:faq.overLimitQ'), a: t('pricingPage:faq.overLimitA') },
            { q: t('pricingPage:faq.trialQ'), a: t('pricingPage:faq.trialA') },
            { q: t('pricingPage:faq.archiveQ'), a: t('pricingPage:faq.archiveA') },
        ];
        if (isInternationalDomain()) {
            items.unshift(
                { q: t('pricingPage:faq.paymentMethodsQ'), a: t('pricingPage:faq.paymentMethodsA') },
                { q: t('pricingPage:faq.cancelAnytimeQ'), a: t('pricingPage:faq.cancelAnytimeA') },
            );
        }
        return items;
    }, [t, i18n.language]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${getApiBaseUrl()}/platform/plans`);
                setPlans((res.data || []).filter((p) => p.isActive !== false));
            } catch {
                setPlans([]);
            }
            setLoading(false);
        })();
    }, []);

    const sorted = [...plans].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const extraPlans = sorted.filter(
        (p) => !p.isCustom && (p.extraStudentPricePerMonth || 0) > 0
    );
    const soloPlan = sorted.find((p) => p.name === 'solo');

    return (
        <div className="page-pricing page-wrapper sp-landing" style={{ minHeight: '100vh' }}>
            <LandingHeader />

            <main className="page-main">
                <div style={{ textAlign: 'center', marginBottom: '36px' }}>
                    <h1 className="sp-section-title" style={{ textAlign: 'center', fontSize: 'clamp(28px, 5vw, 40px)' }}>
                        {t('pricingPage:title')}
                    </h1>
                    <p style={{ margin: 0, color: 'var(--sp-gray-600)', fontSize: '17px', lineHeight: 1.5 }}>
                        {t('pricingPage:subtitle')}<br />
                        {t('pricingPage:subtitleTrial')}
                    </p>
                </div>

                <div className="pricing-toggle-wrap" style={{
                    position: 'relative', left: '50%', transform: 'translateX(-50%)'
                }}>
                    <button type="button" onClick={() => setAnnual(false)} style={toggleBtn(!annual)}>
                        {t('pricingPage:toggleMonthly')}
                    </button>
                    <button type="button" onClick={() => setAnnual(true)} style={toggleBtn(annual)}>
                        {t('pricingPage:toggleAnnual')}{' '}
                        <span style={{ color: annual ? '#a5f3fc' : '#059669', fontWeight: 700 }}>
                            {t('pricingPage:annualDiscountBadge')}
                        </span>
                    </button>
                </div>

                {loading && <p style={{ textAlign: 'center', color: '#888' }}>{t('pricingPage:loading')}</p>}

                <div className="plans-container responsive-grid any-grid">
                    {sorted.map((p) => {
                        const isAcademy = p.name === 'academy';
                        const isCustom = p.isCustom;
                        const monthly = p.monthlyPrice || 0;
                        const annualTotal = p.annualPrice || 0;
                        const annualPerMonth = p.annualMonthlyEquivalent ?? (annualTotal ? annualTotal / 12 : monthly);
                        const displayPrice = annual && !isCustom ? annualPerMonth : monthly;
                        const showStrike = annual && !isCustom && monthly > 0 && annualPerMonth < monthly;
                        const showAnnualTotal = annual && !isCustom && annualTotal > 0;

                        return (
                            <div
                                key={p.id}
                                className={`plan-card${isAcademy ? ' plan-card--featured' : ''}`}
                                style={{
                                    background: '#fff',
                                    borderRadius: '16px',
                                    padding: isAcademy ? '28px 22px' : '22px',
                                    border: isAcademy ? '2px solid #EB3333' : '1px solid #e5e7eb',
                                    boxShadow: isAcademy
                                        ? '0 12px 32px rgba(108,63,232,0.2)'
                                        : '0 2px 10px rgba(0,0,0,0.04)',
                                    position: 'relative',
                                    transform: isAcademy ? 'scale(1.02)' : 'none',
                                    transition: 'box-shadow 0.25s, transform 0.25s'
                                }}
                            >
                                {isAcademy && (
                                    <span style={{
                                        position: 'absolute', top: '-12px', left: '50%',
                                        transform: 'translateX(-50%)',
                                        background: '#EB3333', color: '#fff',
                                        fontSize: '12px', fontWeight: 700,
                                        padding: '5px 14px', borderRadius: '20px', whiteSpace: 'nowrap'
                                    }}>
                                        {t('billing:plans.popular', { defaultValue: t('pricingPage:popularChoice') })}
                                    </span>
                                )}
                                <h3 style={{ margin: '8px 0 4px', fontSize: '20px', fontWeight: 800 }}>{getPlanDisplayName(p, t)}</h3>

                                {isCustom ? (
                                    <div style={{ fontSize: '28px', fontWeight: 800, margin: '16px 0', color: '#1A1A1A' }}>
                                        {t('pricingPage:customPrice')}
                                    </div>
                                ) : (
                                    <div style={{ margin: '16px 0', transition: 'opacity 0.25s' }}>
                                        {showStrike && (
                                            <div style={{
                                                fontSize: '16px', color: '#94a3b8',
                                                textDecoration: 'line-through', marginBottom: '4px'
                                            }}>
                                                {formatPlanPrice(monthly, currency)}{currency.symbol}{t('pricingPage:perMonth')}
                                            </div>
                                        )}
                                        <div style={{ fontSize: '34px', fontWeight: 800, color: '#1A1A1A', lineHeight: 1.1 }}>
                                            {formatPlanPrice(displayPrice, currency)}{currency.symbol}
                                            <span style={{ fontSize: '15px', fontWeight: 500, color: '#888' }}>
                                                {t('pricingPage:perMonth')}
                                            </span>
                                        </div>
                                        {showAnnualTotal && (
                                            <div style={{
                                                marginTop: '10px', fontSize: '15px', fontWeight: 600,
                                                color: '#334155'
                                            }}>
                                                {formatPlanPrice(annualTotal, currency)}{currency.symbol} {t('pricingPage:perYear')}
                                            </div>
                                        )}
                                        {annual && p.annualDiscountPercent > 0 && (
                                            <span style={{
                                                display: 'inline-block', marginTop: '8px',
                                                background: '#ecfdf5', color: '#059669',
                                                padding: '4px 10px', borderRadius: '6px',
                                                fontSize: '12px', fontWeight: 700
                                            }}>
                                                {t('pricingPage:discount17')}
                                            </span>
                                        )}
                                    </div>
                                )}

                                <ul style={{
                                    margin: '0 0 20px', padding: 0, listStyle: 'none',
                                    color: '#475569', fontSize: '14px', lineHeight: 1.8
                                }}>
                                    <li>{t('pricingPage:studentsCount', { limit: fmtLimit(p.baseStudentLimit) })}</li>
                                    <li>{t('pricingPage:schoolsCount', { limit: fmtLimit(p.maxSchools) })}</li>
                                    {!isCustom && (p.extraStudentPricePerMonth || 0) > 0 && (
                                        <li>{t('pricingPage:extraStudentLine', { price: formatPlanPrice(p.extraStudentPricePerMonth, currency) + currency.symbol })}</li>
                                    )}
                                </ul>

                                {isCustom ? (
                                    <a href="mailto:the@surensmail.ru" style={ctaBtn}>
                                        {t('pricingPage:contactUs')}
                                    </a>
                                ) : (
                                    <Link to="/register" style={ctaBtn}>
                                        {t('pricingPage:getStarted', { defaultValue: t('pricingPage:startFree') })}
                                    </Link>
                                )}
                            </div>
                        );
                    })}
                </div>

                {extraPlans.length > 0 && (
                    <section className="pricing-extra-section" style={{
                        marginTop: '48px', background: '#eef2ff',
                        borderRadius: '16px', padding: '32px 28px'
                    }}>
                        <h2 className="page-h2" style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 800, color: '#1A1A1A' }}>
                            {t('pricingPage:extraSectionTitle')}
                        </h2>
                        <p style={{ margin: '0 0 20px', color: '#475569', lineHeight: 1.6, fontSize: '15px' }}>
                            {t('pricingPage:extraSectionBody')}
                        </p>
                        <div className="table-wrapper">
                        <table className="table-cards-mobile" style={{
                            width: '100%', maxWidth: '480px', background: '#fff',
                            borderRadius: '12px', borderCollapse: 'collapse', fontSize: '14px'
                        }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                                    <th style={th}>{t('pricingPage:tablePlan')}</th>
                                    <th style={th}>{t('pricingPage:tableExtraPrice')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {extraPlans.map((p) => (
                                    <tr key={p.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                        <td data-label={t('pricingPage:tablePlan')} style={td}>{getPlanDisplayName(p, t)}</td>
                                        <td data-label={t('pricingPage:tableExtraPrice')} style={td}>
                                            {formatPlanPrice(p.extraStudentPricePerMonth, currency)}{currency.symbol}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="table-scroll-hint">{t('pricingPage:tableScrollHint')}</p>
                        </div>
                    </section>
                )}

                {soloPlan && (
                    <section style={{
                        marginTop: '32px', background: '#fff',
                        borderRadius: '16px', padding: '32px 28px',
                        border: '1px solid #e5e7eb', textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '40px', marginBottom: '12px' }}>🏃</div>
                        <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 800 }}>
                            {t('pricingPage:soloTitle')}
                        </h2>
                        <p style={{
                            margin: '0 auto 20px', maxWidth: '560px',
                            color: '#475569', lineHeight: 1.65, fontSize: '15px'
                        }}>
                            {isRussianDomain()
                                ? t('pricingPage:soloBody', {
                                    limit: soloPlan.baseStudentLimit >= UNLIMITED
                                        ? t('pricingPage:soloUnlimitedStudents')
                                        : soloPlan.baseStudentLimit,
                                })
                                : t('pricingPage:soloBodyPublic', {
                                    limit: soloPlan.baseStudentLimit >= UNLIMITED
                                        ? t('pricingPage:soloUnlimitedStudents')
                                        : soloPlan.baseStudentLimit,
                                    defaultValue: t('pricingPage:soloBody', {
                                        limit: soloPlan.baseStudentLimit >= UNLIMITED
                                            ? t('pricingPage:soloUnlimitedStudents')
                                            : soloPlan.baseStudentLimit,
                                    }),
                                })}
                        </p>
                        <Link to="/register" style={{ ...ctaBtn, display: 'inline-block' }}>
                            {t('pricingPage:tryFree')}
                        </Link>
                    </section>
                )}

                <section style={{ marginTop: '48px' }}>
                    <h2 style={{ textAlign: 'center', marginBottom: '24px', fontSize: '24px', fontWeight: 800 }}>
                        {t('pricingPage:faqTitle')}
                    </h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '720px', margin: '0 auto' }}>
                        {faq.map((item, i) => (
                            <div key={i} style={{
                                background: '#fff', borderRadius: '12px',
                                padding: '20px 24px', border: '1px solid #e5e7eb'
                            }}>
                                <h4 style={{ margin: '0 0 8px', fontSize: '16px', color: '#1A1A1A' }}>{item.q}</h4>
                                <p style={{ margin: 0, color: '#64748b', fontSize: '14px', lineHeight: 1.6 }}>{item.a}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {isInternationalDomain() && (
                    <p style={{
                        textAlign: 'center', marginTop: '32px', color: '#64748b',
                        fontSize: '14px', fontWeight: 500
                    }}>
                        {t('pricingPage:securePayments')}
                    </p>
                )}
            </main>

            <SiteFooter />
        </div>
    );
}

const toggleBtn = (active) => ({
    border: 'none',
    padding: '10px 22px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '14px',
    background: active ? '#EB3333' : 'transparent',
    color: active ? '#fff' : '#64748b',
    transition: 'background 0.2s, color 0.2s'
});

const ctaBtn = {
    display: 'block',
    textAlign: 'center',
    padding: '12px 16px',
    background: '#EB3333',
    color: '#fff',
    borderRadius: '10px',
    textDecoration: 'none',
    fontWeight: 700,
    fontSize: '14px'
};

const th = { padding: '12px 16px', fontWeight: 700, color: '#334155' };
const td = { padding: '12px 16px', color: '#475569' };
